import { Component } from 'react';

import { PageError, type PageErrorType } from '/module/error/page-error';
import { getSentry } from '/module/sentry';

import { PageErrorComponent } from './error';
import css from './style.module.scss';

export class ContainerPage extends Component<{
  children: React.ReactNode;
}> {
  state: {
    hasError?: Error;
  } = {
    hasError: undefined,
  };

  static getDerivedStateFromError(error?: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: error };
  }

  componentDidCatch(error?: Error, errorInfo?: unknown) {
    // Don't log the error because an uncaught error will already show in the browser console

    getSentry().then((sentry) => {
      if (!error || !sentry) {
        return;
      }

      errorInfo && sentry.setExtras(errorInfo as unknown as Record<string, unknown>);
      sentry.captureException(error);
    });
  }

  componentDidUpdate(prevProps: Readonly<{ children: React.ReactNode }>, prevState: Readonly<{ hasError?: Error }>) {
    // Only clear errors when props have changed but not when the error state itself changes
    // This prevents infinite render loops when errors keep occurring
    const hasErrorCurrently = Boolean(this.state.hasError);
    const isSameErrorAsBefore = prevState.hasError === this.state.hasError;
    const childrenChanged = this.props.children !== prevProps.children;

    if (hasErrorCurrently && isSameErrorAsBefore && childrenChanged) {
      this.setState({ hasError: undefined });
    }
  }

  render() {
    const { children } = this.props;
    const { hasError } = this.state;

    const pageError = hasError && hasError instanceof PageError ? hasError : undefined;
    const errorType: PageErrorType = pageError?.type ?? 'error';

    return (
      <div className={css.page}>
        {hasError ? <PageErrorComponent type={errorType} customMsg={pageError?.meta?.customMsg} /> : children}
      </div>
    );
  }
}
